import {
  createElement,
  CSSProperties,
  useRef,
  useState,
  useMemo,
  forwardRef,
  useCallback,
  FocusEvent,
  useLayoutEffect,
  useEffect,
} from 'react';
import { ErrorStatus, WarningStatus } from '@sas/react-svg';
import { createUseHtmlId } from '../../../../hooks/useHtmlId';
import { postfixTestId, TestIdProps } from '../../../../util/testId';
import { useIsRTL } from '../../../../locale';
import {
  OptionPopper,
  OptionPopperOption,
  OptionPopperSeparator,
} from '../../../../components/OptionPopper';
import { isNullish } from '@sas/nova-core/typeguards';
import {
  ReactChangeEvent,
  ReactKeyboardEvent,
  ReactMouseEvent,
} from '../../../../react-synthetic-events';
import { isTruncated } from '../../../../hooks/useTruncationTooltip';
import useOnFocusOut from '../../../../hooks/useOnFocusOut';
import { DesignTokens, renderInlineCssVars, useDesignTokens } from '../../../../theme';
import { GlobalSettingsKeys } from '../../../../components/Settings/GlobalSettingsKeys';
import { useGetGlobalSetting } from '../../../../components/Settings/GlobalSettingsProvider';
import { Icon } from '../../../../components/Icon';
import { setAlpha } from '../../../../design-tokens';
import bundle from '../../NovaComponents-CatalogSearch-gui-icu.properties';
import { createUseTextLocalizer } from '../../../../locale/createUseTextLocalizer';
import { BoxColor, FontStyle, TextColor } from '../../../../theme';
import { useDirectSpeech } from '../../../../components/DirectSpeech/DirectSpeechProvider';
import { setRef } from '../../../../util/ref-util';
import { markEvent } from '../../../../util/events';
import {
  MessagePopover,
  MessagePopoverProps,
} from '../../../../components/Input/__internal__/MessagePopover';
import { SuggestionPopperItem, SuggestionVisibility } from '../shared';
import { getLogger } from '@sas/nova-core/logging';

// import css last
import cx from 'classnames';
import cn from './CatalogInput.css.json';
import './CatalogInput.css';

export type CatalogInputTokens = {
  font?: Partial<FontStyle>;
  theme?: (props: { state: 'normal' | 'warning' | 'alert' }) => Partial<BoxColor> | undefined;
  placeholderTheme?: () => Partial<TextColor> | undefined;
};

type CustomMessage = Pick<MessagePopoverProps, 'minWidth' | 'maxWidth' | 'width' | 'message'>;

type Message = string | null | undefined | CustomMessage;

const isCustomMessage = (message: Message): message is CustomMessage =>
  (message as CustomMessage)?.message !== undefined;

export interface SuggestionSelectionEvent {
  index: number;
}

export interface CatalogInputChangeEvent {
  value: string;
}

export type CatalogInputSuggestionItem = Readonly<
  Omit<SuggestionPopperItem, 'id'> | OptionPopperSeparator
>;

export type CatalogInputCommitEvent = CatalogInputChangeEvent & { reason?: 'blur' | 'enter' };

const useListId = createUseHtmlId('CatalogInput-SuggestionList');
const useListItemId = createUseHtmlId('CatalogInput-SuggestionListItem');
const useValidationMessageId = createUseHtmlId('CatalogInput-ValidationMessage');
const logger = getLogger('CatalogSearchInput');
// There is no global setting for a user-configured double-click delay, so match the double-click delay that table uses to
// determine the difference between a single and double click
// https://gitlab.sas.com/novacommons/nova-commons/-/blob/c810b8c8150cb86c11f273250079154ec9f61f8d/src/nova-commons/src/hooks/useSingleAndDoubleClick/useSingleAndDoubleClick.ts#L122
const DOUBLE_CLICK_DELAY = 200;

export interface CatalogInputProps extends CatalogInputTokens, TestIdProps {
  className?: string;
  style?: CSSProperties;
  placeholder?: string;
  suggestionItems?: null | readonly CatalogInputSuggestionItem[];
  onSuggestionItemSelected?: (event: SuggestionSelectionEvent) => void;
  onHistoryItemDoubleClick?: (event: SuggestionSelectionEvent) => void;
  onCommit?: null | ((event: CatalogInputCommitEvent) => void);
  /**
   * In some cases we need to defer the decision to suppress the escape key when onCancel is used.
   *  If the function returns true or undefined and suggestions is not being displayed, then the
   *  escape key down will be suppressed. The escape key is always suppressed when suggestions is
   *  open because this action closes the suggestions list.
   *
   * Note: Returning true/false onCancel is only needed if a an input is a child of a component
   *  that needs to have it's own escape key handling.
   */
  onCancel?: null | (() => void | boolean);
  status?: null | 'warning' | 'alert';
  /**
   * The error (or warning) message to show when the `status` prop has a value (no null/undefined).
   */
  message?: Message;
  disabled?: boolean;
  hideBorder?: boolean;
  /**
   * use this for prop along with useInputValidationAdapter
   * using alone won't do the validation
   */
  // required?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  onMouseDown?: () => void;
  // NOTE: these should probably be implemented by everyone, so we require them
  onChange: (event: CatalogInputChangeEvent) => void;
  value: string;
  id?: string;
  editable?: boolean;
  maxLength?: number;
  landmarkLabel?: string;
  role?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string[];
  ariaLabelledBy?: string;
  /**
   * @interal
   * TokenInput needs to set aria-invalid to "true" for Input without error status
   * because TokenInput handles the error status on its level
   */
  ariaInvalid?: boolean;
  variant?: 'standard' | 'composite';
  tabindex?: number;
  /**
   * to disable input activate on enter (true by default)
   */
  commitOnEnterInDialog?: boolean;
  showClearButton?: boolean;
  clearButtonTooltip?: string;
  clearButtonAriaLabel?: string;
  suggestionsDisplay?: SuggestionVisibility;
}
const useTextLocalizer = createUseTextLocalizer(bundle);

export const CatalogInput = forwardRef<HTMLInputElement, CatalogInputProps>(
  function CatalogBaseInput({ onHistoryItemDoubleClick, ...props }, ref) {
    const suggestionListRef = useRef<HTMLDivElement | null>(null);
    const [hasFocus, setHasFocus] = useState<boolean>(false);
    const [focusedIndex, setFocusedIndex] = useState<number | null | undefined>(undefined);
    const [hasHover, setHasOver] = useState<boolean>(false);
    const localize = useTextLocalizer();
    const editable = props.editable ?? true;
    const [isFirstFocus, setIsFirstFocus] = useState(true);

    const { suggestionItems, onSuggestionItemSelected } = props;
    const propsOnCommit = props.onCommit;
    const propsOnChange = props.onChange;
    const propsOnCancel = props.onCancel;
    const propsOnFocus = props.onFocus;
    const propsOnMouseDown = props.onMouseDown;
    const propsOnBlur = props.onBlur;
    const propsValue = props.value;
    const commitOnEnterInDialog =
      props.commitOnEnterInDialog !== undefined ? props.commitOnEnterInDialog : true;
    const [shouldShowSuggestions, setShouldShowSuggestions] = useState<boolean>(false);
    const propsSuggestionsDisplay: SuggestionVisibility = props.suggestionsDisplay || 'always';

    // this is for determining the focus index, which is based on the list with *no separators*
    const itemsNoSeparators = useMemo(() => {
      return suggestionItems?.filter((si) => !(si as OptionPopperSeparator).separator);
    }, [suggestionItems]);

    const isRTL = useIsRTL();

    const closeSuggestions = useCallback(() => {
      setFocusedIndex(undefined);
      setShouldShowSuggestions(false);
    }, [setFocusedIndex, setShouldShowSuggestions]);

    const noSeparatorIndexToSuggestionIndex = useCallback(
      (selectedIndex: number): number | undefined => {
        if (
          !itemsNoSeparators ||
          !suggestionItems ||
          selectedIndex >= itemsNoSeparators.length ||
          selectedIndex < 0
        ) {
          return;
        }
        return suggestionItems.indexOf(itemsNoSeparators[selectedIndex]);
      },
      [itemsNoSeparators, suggestionItems]
    );

    const selectSuggestionHandler = useCallback(
      (selectedIndex: number) => {
        const suggestionIndex = noSeparatorIndexToSuggestionIndex(selectedIndex);

        if (suggestionIndex === undefined) {
          return;
        }

        onSuggestionItemSelected?.({ index: suggestionIndex });
        const activeSuggestion = suggestionItems?.[suggestionIndex];
        if ((activeSuggestion as SuggestionPopperItem)?.suggestionType !== 'catalog') {
          // keep the suggestions popper open for suggestions that are not history or help suggestions
          closeSuggestions();
        }
      },
      [
        noSeparatorIndexToSuggestionIndex,
        onSuggestionItemSelected,
        closeSuggestions,
        suggestionItems,
      ]
    );

    const refInput = useRef<HTMLInputElement | null>(null);
    const setRefInput = useCallback(
      (input: HTMLInputElement | null) => {
        refInput.current = input;
        setRef(ref, input);
      },
      [ref]
    );

    const refList = useRef<HTMLUListElement | null>(null);

    useLayoutEffect(() => {
      if (
        shouldShowSuggestions &&
        suggestionListRef?.current &&
        refInput.current &&
        refList.current
      ) {
        suggestionListRef.current.style.maxWidth = `${
          refInput.current.getBoundingClientRect().width
        }px`;
        refList.current.style.maxWidth = `${refInput.current.getBoundingClientRect().width}px`;
      }
    }, [shouldShowSuggestions, suggestionListRef, refInput, refList]);

    const handlers = useOnFocusOut(() => {
      closeSuggestions(); // might be triggering a react error about state updates on unmounted component...
      setHasFocus(false);
      if (focusedIndex === undefined || focusedIndex === null) {
        propsOnCommit?.({ value: propsValue, reason: 'blur' });
      }
    });

    const handlersOnBlur = handlers.onBlur;
    const handlersOnFocus = handlers.onFocus;
    const onBlur = useCallback(
      (e: FocusEvent<HTMLDivElement>) => {
        if (!suggestionListRef.current?.contains(e.relatedTarget as Node)) {
          handlersOnBlur();
          propsOnBlur?.();
          // Added for NOVACOMMONS-966, text remains selcted when focus out via tab navigation
          !editable && window.getSelection()!.empty();
          setHasOver(false);
          setHasFocus(false);
        }
      },
      [editable, propsOnBlur, handlersOnBlur]
    );

    const handleFocusOnInput = useCallback(() => {
      handlersOnFocus();
      setHasFocus(true);
      if (
        propsSuggestionsDisplay === 'always' ||
        (propsSuggestionsDisplay === 'secondFocus' && !isFirstFocus)
      ) {
        setShouldShowSuggestions(true);
      }
      if (isFirstFocus) {
        setIsFirstFocus(false);
      }

      propsOnFocus?.();
    }, [handlersOnFocus, isFirstFocus, propsSuggestionsDisplay, propsOnFocus]);

    const onFocus = useCallback(
      (e: FocusEvent<HTMLDivElement>) => {
        if (refInput.current && e.target !== refInput.current) {
          refInput.current.focus();
          return;
        }
        handleFocusOnInput();
      },
      [handleFocusOnInput]
    );

    const getNextIndex = useCallback(
      (
        suggItems: ReadonlyArray<CatalogInputSuggestionItem> | undefined,
        prevIndex: number | null | undefined,
        direction: 'increment' | 'decrement'
      ): number | undefined => {
        if (!suggItems) {
          return undefined;
        }
        let nextIndex = 0;
        if (direction === 'decrement') {
          if (!isNullish(prevIndex)) {
            nextIndex = Math.max(prevIndex - 1, 0);
          } else {
            nextIndex = suggItems.length - 1;
          }
        } else {
          if (!isNullish(prevIndex) && !!suggItems) {
            nextIndex = Math.min(prevIndex + 1, suggItems.length - 1);
          }
        }
        if ((suggItems[nextIndex] as OptionPopperSeparator).separator) {
          // this is a separator, it doesn't count when focus changes, keep going!
          return getNextIndex(suggItems, nextIndex, direction);
        }
        return nextIndex;
      },
      []
    );

    const getNextSuggestionAndFocusIndices = useCallback(
      (
        prevFocusedIndex: number | null | undefined,
        direction: 'increment' | 'decrement'
      ): {
        nextFocusedIndex: number | null | undefined;
        nextSuggestionIndex: number | null | undefined;
      } => {
        if (!suggestionItems || !itemsNoSeparators) {
          return { nextFocusedIndex: undefined, nextSuggestionIndex: undefined };
        }
        const nextFocusedIndex = getNextIndex(itemsNoSeparators, prevFocusedIndex, direction);
        const prevSuggestionIndex = prevFocusedIndex
          ? suggestionItems.indexOf(itemsNoSeparators[prevFocusedIndex])
          : undefined;
        const nextSuggestionIndex = getNextIndex(suggestionItems, prevSuggestionIndex, direction);
        return { nextFocusedIndex, nextSuggestionIndex };
      },
      [suggestionItems, itemsNoSeparators, getNextIndex]
    );

    const onMouseDown = useCallback(() => {
      propsOnMouseDown?.();
    }, [propsOnMouseDown]);

    const handleKeyDown = useCallback(
      (e: ReactKeyboardEvent<HTMLInputElement>) => {
        // commitOnEnterInDialog (true by default), keeps the markEvent from happening thus triggering the default action.
        // Marking this event is needed when Input is in a Dialog so that Enter doesn't trigger the Dialog's default button
        if (commitOnEnterInDialog && e.key === 'Enter') {
          markEvent(e);
        }
        if (e.key === 'Escape') {
          closeSuggestions();
          const cancelHandled = propsOnCancel?.();
          if (shouldShowSuggestions || (propsOnCancel && cancelHandled !== false)) {
            e.preventDefault();
            e.stopPropagation();
          }
          return;
        }
        if (
          !shouldShowSuggestions &&
          e.key !== 'Enter' &&
          isPrintableKey(e.key) &&
          propsSuggestionsDisplay !== 'never'
        ) {
          setShouldShowSuggestions(true);
          // prevent text input cursor from moving if key was space.
          // this means the space bar was the first key entered.
          if (propsValue === '' && propsSuggestionsDisplay === 'secondFocus' && e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
          }
        }
        if (!shouldShowSuggestions || !suggestionItems || suggestionItems.length === 0) {
          return;
        }
        if (e.key === 'ArrowDown') {
          // prevent text input cursor from moving
          e.preventDefault();
          e.stopPropagation();
          const nextIndices = getNextSuggestionAndFocusIndices(focusedIndex, 'increment');
          setFocusedIndex(nextIndices.nextFocusedIndex);
        } else if (e.key === 'ArrowUp') {
          // prevent text input cursor from moving
          e.preventDefault();
          e.stopPropagation();
          const nextIndices = getNextSuggestionAndFocusIndices(focusedIndex, 'decrement');
          setFocusedIndex(nextIndices.nextFocusedIndex);
        }
      },
      [
        closeSuggestions,
        commitOnEnterInDialog,
        focusedIndex,
        getNextSuggestionAndFocusIndices,
        propsOnCancel,
        propsSuggestionsDisplay,
        propsValue,
        shouldShowSuggestions,
        suggestionItems,
      ]
    );

    const handleChange = useCallback(
      (e: ReactChangeEvent<HTMLInputElement>) => {
        const liveValue = e.target.value;
        propsOnChange({ value: liveValue });
        setFocusedIndex(undefined);
      },
      [propsOnChange]
    );

    const handleKeyPress = useCallback(
      (event: ReactKeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();

          if (!isNullish(focusedIndex) && !!itemsNoSeparators && suggestionItems) {
            const suggestionIndex = suggestionItems.indexOf(itemsNoSeparators[focusedIndex]);
            const currentSuggestion = suggestionItems[suggestionIndex];
            const currentSuggestionText = (currentSuggestion as OptionPopperOption).text;
            const suggestionType = (currentSuggestion as SuggestionPopperItem).suggestionType;
            if (!!currentSuggestionText) {
              onSuggestionItemSelected?.({ index: suggestionIndex });
              if (suggestionType && suggestionType !== 'catalog') {
                closeSuggestions();
              }
              return;
            }
          }
          closeSuggestions();
          propsOnCommit?.({ reason: 'enter', value: propsValue });
        }
      },
      [
        closeSuggestions,
        focusedIndex,
        suggestionItems,
        itemsNoSeparators,
        onSuggestionItemSelected,
        propsOnCommit,
        propsValue,
      ]
    );

    const handleMouseOver = useCallback(
      (e: ReactMouseEvent) => {
        setHasOver(true);
        const input = e.target as HTMLInputElement;
        const content = input?.value;
        if (content?.length && isTruncated(input, content)) {
          input.setAttribute('title', content);
        } else {
          input.removeAttribute('title');
        }
      },
      [setHasOver]
    );

    const handleMouseLeave = useCallback(() => setHasOver(false), []);

    const handleOnClick = useCallback(() => {
      // this is meant for when the search field isn't showing the suggestions
      // popover and the user clicks on the search field to show it.
      if (!shouldShowSuggestions && propsSuggestionsDisplay !== 'never') {
        setShouldShowSuggestions(true);
      }
    }, [propsSuggestionsDisplay, shouldShowSuggestions]);

    const dt = useDesignTokens();
    const styleTokenOverrideValues = useMemo(() => {
      const overrides = props.theme;
      const placeholder = props.placeholderTheme;
      let overrideValues = {};
      if (overrides) {
        overrideValues = {
          ...renderInlineCssVars(overrides({ state: 'normal' }), 'input'),
          ...renderInlineCssVars(overrides({ state: 'alert' }), 'input-alert'),
          ...renderInlineCssVars(overrides({ state: 'warning' }), 'input-warning'),
        };
      }
      if (placeholder) {
        overrideValues = {
          ...overrideValues,
          ...renderInlineCssVars(placeholder(), 'input-placeholder'),
        };
      }
      return overrideValues;
    }, [props.theme, props.placeholderTheme]);

    const isCustomFocusTurnedOn = useGetGlobalSetting(GlobalSettingsKeys.FOCUS_OVERRIDE) === 'true';

    // Outline Focus Indicator
    let outline;
    let border;
    if (
      dt.themeVariant !== 'high-contrast' &&
      editable &&
      !props.disabled &&
      !isCustomFocusTurnedOn
    ) {
      outline = 'none';
    }
    if ((dt.themeVariant === 'high-contrast' || props.disabled) && props.variant === 'composite') {
      border = 'transparent';
    }

    const style = {
      ...renderInlineCssVars(getInputThemeTokens(dt, props.status), 'input'),
      ...renderInlineCssVars(
        {
          'pd-squish-s': dt.spacing.insetSquish.s.padding,
          'mg-xs': dt.spacing.core.xs,
        },
        'input'
      ),
      ...renderInlineCssVars({ outline, 'noneditable-bd': border }, 'input-focus'),
      ...styleTokenOverrideValues,
      ...props.font,
    };

    const idList = useListId();
    const listItemIds = useListItemId(suggestionItems ? suggestionItems.length : 0);
    const idValidationMessage = useValidationMessageId();

    const ariaDescribedBy = [];
    if (props.status) ariaDescribedBy.push(idValidationMessage);
    if (props.ariaDescribedBy) ariaDescribedBy.push(...props.ariaDescribedBy);

    const historyItemDoubleClickRef = useRef<[number, number] | undefined>();
    const onListItemSuggestionSelected = useCallback(
      ({ index }: { index: any }) => {
        if (onHistoryItemDoubleClick) {
          logger.debug('history double click check is enabled');
          const suggestionIndex = noSeparatorIndexToSuggestionIndex(index);
          logger.debug(`suggestionIndex: ${suggestionIndex}`);
          if (suggestionIndex) {
            if (
              (suggestionItems?.[suggestionIndex] as SuggestionPopperItem)?.suggestionType ===
              'history'
            ) {
              logger.debug('history item detected');
              // history items can be double-clicked to immediately execute a search, so we need to delay selecting the suggestion for enough time
              // to allow a double-click to be detected.
              if (historyItemDoubleClickRef.current) {
                logger.debug(`doubleclick detected on:  ${suggestionIndex}`);
                const [timeoutHandle, ix] = historyItemDoubleClickRef.current;
                historyItemDoubleClickRef.current = undefined;
                clearTimeout(timeoutHandle);
                if (ix === index) {
                  const suggIx = noSeparatorIndexToSuggestionIndex(ix);
                  if (suggIx !== undefined) {
                    onHistoryItemDoubleClick({ index: suggIx });
                    closeSuggestions();
                  }
                }
                return;
              }
              const handle = setTimeout(
                (() => {
                  logger.debug(`entering timeout handler`);
                  if (historyItemDoubleClickRef.current) {
                    const [h, i] = historyItemDoubleClickRef.current;
                    historyItemDoubleClickRef.current = undefined;
                    clearTimeout(h);
                    selectSuggestionHandler(i);
                    logger.debug(`history item ${i} was single-clicked`);
                  }
                }) as TimerHandler,
                DOUBLE_CLICK_DELAY
              );
              historyItemDoubleClickRef.current = [handle, index];
              return;
            }
          }
        }
        logger.debug(() => `single click detected on:  ${index} at ${Date.now()}`);
        selectSuggestionHandler(index);
      },
      [
        selectSuggestionHandler,
        suggestionItems,
        noSeparatorIndexToSuggestionIndex,
        onHistoryItemDoubleClick,
        closeSuggestions,
      ]
    );

    let narratorMessage = '';
    if (itemsNoSeparators?.length === 0) {
      narratorMessage = localize('narrator.noresults.msg');
    } else if (shouldShowSuggestions) {
      if (itemsNoSeparators?.length === 1) narratorMessage = localize('narrator.result.msg');
      else {
        narratorMessage = localize('narrator.results.msg', {
          numResults: itemsNoSeparators?.length.toString() || '',
        });
      }
    }

    const directSpeech = useDirectSpeech();
    useEffect(() => {
      if (shouldShowSuggestions || itemsNoSeparators?.length === 0) {
        directSpeech?.notify(narratorMessage);
      }
    }, [directSpeech, shouldShowSuggestions, itemsNoSeparators?.length, narratorMessage]);

    const op = useMemo(() => {
      return (
        <OptionPopper
          ref={suggestionListRef}
          focusedIndex={focusedIndex}
          listId={idList}
          isOpen={shouldShowSuggestions}
          referenceElementRef={refInput}
          displayListItemIcon
          ulRef={refList}
          items={
            !!suggestionItems ? suggestionItems.map((s, i) => ({ ...s, id: listItemIds[i] })) : []
          }
          onSelectFocusedItem={() => {
            if (!isNullish(focusedIndex)) {
              selectSuggestionHandler(focusedIndex);
            }
          }}
          onSelected={onListItemSuggestionSelected}
          listItemFocusable
          testId={postfixTestId(props.testId, 'suggestionPopper')}
          focusLockProps={{ disabled: true }}
        />
      );
    }, [
      shouldShowSuggestions,
      suggestionListRef,
      focusedIndex,
      idList,
      refInput,
      refList,
      suggestionItems,
      selectSuggestionHandler,
      onListItemSuggestionSelected,
      props.testId,
      listItemIds,
    ]);

    return (
      <div
        className={cx(cn.container, props.className)}
        style={{ ...style, ...props.style }}
        onBlur={onBlur}
        onFocus={onFocus}
        onMouseDown={onMouseDown}
        data-testid={props.testId}
      >
        <input
          style={props.font}
          tabIndex={props.tabindex}
          className={cx(
            cn.input,
            props.hideBorder && cn.hideBorder,
            props.status && cn.hasInputIcon,
            props.disabled && cn.disabled,
            !editable && cn.readonly,
            props.variant === 'composite' &&
              props.status === undefined &&
              !editable &&
              cn.composite,
            props.variant === 'composite' && !editable && cn.nonEditableInput,
            isRTL && cn.rtl
          )}
          data-testid={postfixTestId(props.testId, 'input')}
          ref={setRefInput}
          value={propsValue}
          placeholder={!editable ? undefined : props.placeholder!}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onKeyPress={handleKeyPress}
          onMouseEnter={handleMouseOver}
          onMouseLeave={handleMouseLeave}
          onClick={handleOnClick}
          onFocus={() => {
            setHasFocus(true);
          }}
          type={'text'}
          spellCheck="false"
          autoComplete="off"
          // Set aria-autocomplete='list' here if there are suggestions displayed (for now).  This setting should be changed to be aria-autocomplete='both'
          // in the future when the highlighted "type-ahead" functionality is added to the Input (when only one filtered selection exists). This directive
          // is per the recent Accessibility Clinic review of Input (8/10/2021) with Jesse Sookne/Greg Kraus.
          aria-autocomplete={shouldShowSuggestions ? 'list' : undefined}
          aria-haspopup={shouldShowSuggestions ? true : undefined}
          aria-disabled={props.disabled}
          aria-invalid={props.status === 'alert' ? true : undefined}
          aria-describedby={ariaDescribedBy.length > 0 ? ariaDescribedBy.join(' ') : undefined}
          aria-controls={shouldShowSuggestions ? idList : undefined}
          aria-activedescendant={
            shouldShowSuggestions &&
            !isNullish(focusedIndex) &&
            noSeparatorIndexToSuggestionIndex(focusedIndex) !== undefined
              ? listItemIds[noSeparatorIndexToSuggestionIndex(focusedIndex)!]
              : undefined
          }
          aria-label={props.ariaLabel}
          aria-labelledby={props.ariaLabelledBy}
          id={props.id}
          readOnly={!editable || props.disabled}
          aria-readonly={!editable}
          maxLength={props.maxLength}
          data-landmark-label={props.landmarkLabel}
          role={props.role}
        />

        {props.status ? (
          <span aria-live="off" role="status" style={{ width: '0' }}>
            <span className={cn.offscreen} id={idValidationMessage}>
              {localize('invalidInput.msg', {
                message: isCustomMessage(props.message) ? props.message.message : props.message!,
              })}
            </span>
          </span>
        ) : null}
        {!hasFocus && props.status ? (
          <div
            className={cn.inputIcon}
            onMouseEnter={handleMouseOver}
            onMouseLeave={handleMouseLeave}
          >
            <Icon
              icon={props.status === 'alert' ? ErrorStatus : WarningStatus}
              color={props.status}
            />
          </div>
        ) : null}
        {op}
        {props.status ? (
          <MessagePopover
            status={props.status}
            referenceElementRef={refInput}
            {...(isCustomMessage(props.message) ? props.message : { message: props.message! })}
            hidden={!hasFocus && !hasHover}
            testId={postfixTestId(props.testId, 'messagePopper')}
          />
        ) : null}
      </div>
    );
  }
);

export const getInputThemeTokens = (dt: DesignTokens, status: CatalogInputProps['status']) => {
  if (dt.enableNovaTokens) {
    if (status) {
      return {
        ...dt.stateStatusNova[status],
        'placeholder-fg': dt.c.colorTextLabel,
        'selection-bg': dt.textNova.selection.bg,
        'selection-fg': dt.textNova.selection.fg,
        'disabled-opacity': 1,
        'disabled-fg': dt.stateInputNova.disabled.fg,
        'disabled-bd': dt.stateInputNova.disabled.bd,
        'disabled-bg': dt.stateInputNova.disabled.bg,
        'noneditable-bg': dt.stateInputNova.nonEditable.bg,
        'noneditable-bd': dt.stateInputNova.nonEditable.bd,
      };
    } else {
      return {
        ...dt.stateInputNova.normal,
        'selection-bg': dt.textNova.selection.bg,
        'selection-fg': dt.textNova.selection.fg,
        'disabled-opacity': 1,
        'focus-bd': dt.stateInputNova.focused.bd,
        'placeholder-fg': dt.c.colorTextLabel,
        'readonly-bg': dt.stateInputNova.readOnly.bg,
        'readonly-bd': dt.stateInputNova.readOnly.bd,
        'readonly-fg': dt.stateInputNova.readOnly.fg,
        'noneditable-bg': dt.stateInputNova.nonEditable.bg,
        'noneditable-bd': dt.stateInputNova.nonEditable.bd,
        'noneditable-fg': dt.stateInputNova.nonEditable.fg,
        'disabled-fg': dt.stateInputNova.disabled.fg,
        'disabled-bd': dt.stateInputNova.disabled.bd,
        'disabled-bg': dt.stateInputNova.disabled.bg,
      };
    }
  } else {
    // legacy tokens
    if (status) {
      const statusTokens = {
        ...dt.stateStatus[status],
        'selection-bg': dt.text.selection.bg,
        'selection-fg': dt.text.selection.fg,
      };

      if (
        dt.baseReportThemeName === 'highcontrast2017' ||
        dt.baseReportThemeName === 'highcontrast2018'
      ) {
        return {
          ...statusTokens,
          'placeholder-fg': dt.c.hcDisabled,
          'disabled-opacity': 0.5,
        };
      }

      switch (dt.themeVariant) {
        case 'dark':
          return {
            ...statusTokens,
            'placeholder-fg': setAlpha(dt.c.white, 0.3),
            'disabled-opacity': 0.25,
          };
        case 'high-contrast':
          return {
            ...statusTokens,
            'placeholder-fg': setAlpha(dt.c.white, 0.5),
            'disabled-opacity': 0.5,
          };
        default:
          return {
            ...statusTokens,
            'placeholder-fg': dt.c.n1,
            'disabled-opacity': 0.3,
          };
      }
    } else {
      if (
        dt.baseReportThemeName === 'highcontrast2017' ||
        dt.baseReportThemeName === 'highcontrast2018'
      ) {
        return {
          bd: dt.c.white,
          bg: dt.c.black,
          fg: dt.c.white,

          'selection-bg': dt.text.selection.bg,
          'selection-fg': dt.text.selection.fg,

          'placeholder-fg': dt.c.hcDisabled,
          'disabled-opacity': 0.5,
          'focus-bd': dt.c.p0,
        };
      }

      switch (dt.themeVariant) {
        case 'dark':
          return {
            bd: setAlpha(dt.c.white, 0.3),
            bg: dt.c.p_3,
            fg: dt.c.white,

            'selection-bg': dt.text.selection.bg,
            'selection-fg': dt.text.selection.fg,

            'placeholder-fg': setAlpha(dt.c.white, 0.3),
            'disabled-opacity': 0.25,
            'focus-bd': dt.c.p0,
          };
        case 'high-contrast':
          return {
            bd: dt.c.white,
            bg: dt.c.black,
            fg: dt.c.white,

            'selection-bg': dt.text.selection.bg,
            'selection-fg': dt.text.selection.fg,

            'placeholder-fg': setAlpha(dt.c.white, 0.5),
            'disabled-opacity': 0.5,
            'focus-bd': dt.c.p3,
          };
        default:
          return {
            bd: dt.c.n1,
            bg: dt.c.white,
            fg: dt.c.n_3,

            'selection-bg': dt.text.selection.bg,
            'selection-fg': dt.text.selection.fg,

            'placeholder-fg': dt.c.n1,
            'disabled-opacity': 0.3,
            'focus-bd': dt.c.p0,
          };
      }
    }
  }
};

// https://www.w3.org/TR/uievents-key/#named-key-attribute-values
// this is not quite exhaustive, but I want to be careful about excluding international or composed characters
const isPrintableKey = (key: string): boolean => {
  return ![
    'Alt',
    'AltGraph',
    'CapsLock',
    'Control',
    'Fn',
    'FnLock',
    'Meta',
    'NumLock',
    'ScrollLock',
    'Shift',
    'Symbol',
    'SymbolLock',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'End',
    'Home',
    'PageDown',
    'PageUp',
    'Meta',
    'NumLock',
    'ScrollLock',
    'Shift',
    'Symbol',
    'SymbolLock',
    'Copy',
    'CrSel',
    'Cut',
    'Delete',
    'EraseEof',
    'ExSel',
    'Insert',
    'Accept',
    'Again',
    'Attn',
    'Cancel',
    'ContextMenu',
    'Escape',
    'Execute',
    'Find',
    'Help',
    'Pause',
    'Play',
    'Props',
    'Select',
    'ZoomIn',
    'ZoomOut',
    'BrightnessDown',
    'BrightnessUp',
    'Eject',
    'LogOff',
    'Power',
    'WakeUp',
    'ChannelDown',
    'ChannelUp',
    'Close',
    'MailForward',
    'MailReply',
    'MailSend',
    'MediaClose',
    'MediaFastForward',
    'MediaPause',
    'MediaPlay',
    'MediaPlayPause',
    'MediaRecord',
    'MediaRewind',
    'MediaStop',
    'MediaTrackNext',
    'MediaTrackPrevious',
    'New',
    'Open',
    'Print',
    'Save',
    'SpellCheck',
    'AudioBalanceLeft',
    'AudioBalanceRight',
    'AudioBassBoostDown',
    'AudioBassBoostToggle',
    'AudioBassBoostUp',
    'AudioFaderFront',
    'AudioFaderRear',
    'AudioSurroundModeNext',
    'AudioTrebleDown',
    'AudioTrebleUp',
    'AudioVolumeDown',
    'AudioVolumeUp',
    'AudioVolumeMute',
    'MicrophoneToggle',
    'MicrophoneVolumeDown',
    'MicrophoneVolumeUp',
    'MicrophoneVolumeMute',
    'SpeechCorrectionList',
    'SpeechInputToggle',
    'LaunchApplication1',
    'LaunchApplication2',
    'LaunchCalendar',
    'LaunchContacts',
    'LaunchMail',
    'LaunchMediaPlayer',
    'LaunchMusicPlayer',
    'LaunchPhone',
    'LaunchScreenSaver',
    'LaunchSpreadsheet',
    'LaunchWebBrowser',
    'LaunchWebCam',
    'LaunchWordProcessor',
    'BrowserBack',
    'BrowserFavorites',
    'BrowserForward',
    'BrowserHome',
    'BrowserRefresh',
    'BrowserSearch',
    'BrowserStop',
    'AppSwitch',
    'Call',
    'Camera',
    'CameraFocus',
    'EndCall',
    'GoBack',
    'GoHome',
    'HeadsetHook',
    'LastNumberRedial',
    'Notification',
    'MannerMode',
    'VoiceDial',
    'TV',
    'TV3DMode',
    'TVAntennaCable',
    'TVAudioDescription',
    'TVAudioDescriptionMixDown',
    'TVAudioDescriptionMixUp',
    'TVContentsMenu',
    'TVDataService',
    'TVInput',
    'TVInputComponent1',
    'TVInputComponent2',
    'TVInputComposite1',
    'TVInputComposite2',
    'TVInputHDMI1',
    'TVInputHDMI2',
    'TVInputHDMI3',
    'TVInputHDMI4',
    'TVInputVGA1',
    'TVMediaContext',
    'TVNetwork',
    'TVNumberEntry',
    'TVPower',
    'TVRadioService',
    'TVSatellite',
    'TVSatelliteBS',
    'TVSatelliteCS',
    'TVSatelliteToggle',
    'TVTerrestrialAnalog',
    'TVTerrestrialDigital',
    'TVTimer',
  ].includes(key);
};
